// Define TranscriptionSource enum locally to avoid circular dependency
export enum TranscriptionSource {
  NONE = 'none',
  WEB_DEEPGRAM = 'web-deepgram',
  WEB_GEMINI_FLASH = 'web-gemini-flash',
  DESKTOP_DEEPGRAM = 'desktop-deepgram',
  DESKTOP_AWS_TRANSCRIBE = 'desktop-aws-transcribe',
  LOCAL_STT = 'local-stt',
  ZOOM_MEETING = 'zoom_meeting',
}

// Audio handler types
export type AudioHandlerType = 'tauri' | 'browser' | 'deepgram';

export interface TranscriptionSourceConfig {
  supportsPartialChunks: boolean;
  enableFillerPhraseFiltering: boolean;
  enableDeduplication: boolean;
  customFillerPhrases?: string[];
  enableSpeakerMapping: boolean;
  audioHandler: AudioHandlerType;
}


export const COMMON_FILLER_PHRASES = [
  'Thank you.',
  'Thank you!',
  "Thank you. Thank you.",
  'Thanks for watching.',
  'Thank you for watching.',
  'Thank you for watching!',
  'Thanks for watching!',
  'Thanks for watching! Bye!',
  'Please subscribe!',
  "We'll be right back.",
  "Okay. Thank you.",
  'Bye!',
] as const;


export const TRANSCRIPTION_SOURCE_CONFIG: Record<TranscriptionSource, TranscriptionSourceConfig> = {
  [TranscriptionSource.NONE]: {
    supportsPartialChunks: false,
    enableFillerPhraseFiltering: false,
    enableDeduplication: false,
    enableSpeakerMapping: false,
    audioHandler: 'browser',
  },

  [TranscriptionSource.WEB_DEEPGRAM]: {
    supportsPartialChunks: true,
    enableFillerPhraseFiltering: false,
    enableDeduplication: true,
    enableSpeakerMapping: true,
    audioHandler: 'browser',
  },

  [TranscriptionSource.WEB_GEMINI_FLASH]: {
    supportsPartialChunks: true,
    enableFillerPhraseFiltering: false,
    enableDeduplication: true,
    enableSpeakerMapping: true,
    audioHandler: 'browser',
  },

  [TranscriptionSource.DESKTOP_DEEPGRAM]: {
    supportsPartialChunks: true,
    enableFillerPhraseFiltering: false,
    enableDeduplication: true,
    enableSpeakerMapping: true,
    audioHandler: 'tauri',
  },

  [TranscriptionSource.DESKTOP_AWS_TRANSCRIBE]: {
    supportsPartialChunks: true,
    enableFillerPhraseFiltering: false,
    enableDeduplication: true,
    enableSpeakerMapping: true,
    audioHandler: 'tauri',
  },

  [TranscriptionSource.LOCAL_STT]: {
    supportsPartialChunks: false,
    enableFillerPhraseFiltering: true,
    enableDeduplication: false,
    enableSpeakerMapping: true,
    audioHandler: 'tauri',
  },

  [TranscriptionSource.ZOOM_MEETING]: {
    supportsPartialChunks: false,
    enableFillerPhraseFiltering: false,
    enableDeduplication: false,
    enableSpeakerMapping: true,
    audioHandler: 'browser',
    customFillerPhrases: [
      // Zoom-specific phrases that should NOT be filtered
      // (empty for now, but shows how to add source-specific config)
    ],
  },
};


export function getSourceConfig(source: TranscriptionSource): TranscriptionSourceConfig {
  return TRANSCRIPTION_SOURCE_CONFIG[source];
}


export function supportsPartialChunks(source: TranscriptionSource): boolean {
  return getSourceConfig(source).supportsPartialChunks;
}





export function getFillerPhrases(source: TranscriptionSource): readonly string[] {
  const config = getSourceConfig(source);
  if (!config.enableFillerPhraseFiltering) {
    return [];
  }

  const customPhrases = config.customFillerPhrases || [];
  return [...COMMON_FILLER_PHRASES, ...customPhrases];
}


export function isFillerPhrase(text: string, source: TranscriptionSource): boolean {
  const fillerPhrases = getFillerPhrases(source);
  return fillerPhrases.includes(text.trim());
}


export function getAudioHandlerType(source: TranscriptionSource): AudioHandlerType {
  return getSourceConfig(source).audioHandler;
}
